import {
  earningsItemSchema,
  economicEventOutputSchema,
  entitySchema,
  keyDevsObjectSchema,
} from '@api';
import {
  isSvgIconName,
  type SvgIconNames,
} from '@design/design-tokens/iconography/SvgIcons';
import type { ReactNode } from 'react';
import { z } from 'zod';

/**
 * Generative-UI responses are intentionally a tree of named blocks.
 * The producer (today: the Alfred agent; tomorrow: any structured
 * generator) picks a `component` from a curated allow-list, and the
 * renderer maps that name to a React component via `registry.tsx`.
 *
 * This file is the runtime contract for untrusted JSON. Keep each block
 * schema JSON-serializable and intentionally narrower than the underlying
 * React component props: no callbacks, refs, React nodes, arbitrary DOM
 * attributes, or agent-controlled renderer overrides.
 */
export const GENERATIVE_UI_BLOCK_NAMES = [
  'Markdown',
  'Chart',
  'TimeSeriesInfo',
  'AssetOverviewHeader',
  'PriceDisplay',
  'Calendar',
  'Card',
  'Badge',
  'Accordion',
  'Table',
  'Resizable',
  'Stack',
  'Row',
  'Separator',
] as const;

export type GenerativeUiBlockName = (typeof GENERATIVE_UI_BLOCK_NAMES)[number];

const MAX_BLOCKS_PER_LEVEL = 100;
const MAX_MARKDOWN_LENGTH = 100_000;
const MAX_TABLE_COLUMNS = 20;
const MAX_TABLE_ROWS = 500;
const MAX_CHART_SERIES = 20;
const MAX_CHART_POINTS_PER_SERIES = 5_000;
const MAX_CALENDAR_EVENTS_PER_KIND = 100;
const MAX_CALENDAR_ENTITIES = 100;

const chartV2HorizonSchema = z.enum([
  '1D',
  '1W',
  '2W',
  '1M',
  '2M',
  '3M',
  '6M',
  '1Y',
  '3Y',
  '5Y',
  '10Y',
  'MAX',
]);

const chartV2IntervalSchema = z.enum([
  '1m',
  '5m',
  '15m',
  '30m',
  '1h',
  '1D',
  '1W',
  '1M',
]);

const chartV2ThemeSchema = z.record(z.string(), z.string());

const chartV2LocalesSchema = z
  .object({
    searchAssets: z.string().optional(),
    clearAll: z.string().optional(),
    chartType: z.string().optional(),
    interval: z.string().optional(),
    horizon: z.string().optional(),
    noData: z.string().optional(),
    loading: z.string().optional(),
    error: z.string().optional(),
    legendMore: z.string().optional(),
    legendHide: z.string().optional(),
    legendShow: z.string().optional(),
    legendRemove: z.string().optional(),
    priceDisplayPrice: z.string().optional(),
    priceDisplayIndexed: z.string().optional(),
    contextMenuDisplaySplitYAxis: z.string().optional(),
    contextMenuDisplayMergedYAxis: z.string().optional(),
    contextMenuResetChart: z.string().optional(),
    legendMenuChangeColor: z.string().optional(),
    legendMenuMoveUp: z.string().optional(),
    legendMenuMoveDown: z.string().optional(),
    legendMenuSeriesTypeLine: z.string().optional(),
    legendMenuSeriesTypeArea: z.string().optional(),
    legendMenuSeriesTypeCandlestick: z.string().optional(),
    legendMenuSeriesTypeBar: z.string().optional(),
    zoomInToView: z.string().optional(),
  })
  .strict();

const chartV2LinePointSchema = z
  .object({
    time: z.number().finite(),
    value: z.number().finite(),
  })
  .strict();

const chartV2OhlcPointSchema = z
  .object({
    time: z.number().finite(),
    open: z.number().finite(),
    high: z.number().finite(),
    low: z.number().finite(),
    close: z.number().finite(),
  })
  .strict();

const chartV2SeriesBaseSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    yAxisLabel: z.string().optional(),
    paneId: z.string().optional(),
    showLatestPrice: z.boolean().optional(),
  })
  .strict();

const chartV2LineSeriesSchema = chartV2SeriesBaseSchema
  .extend({
    seriesType: z.enum(['line', 'area']),
    data: z.array(chartV2LinePointSchema).max(MAX_CHART_POINTS_PER_SERIES),
  })
  .strict();

const chartV2OhlcSeriesSchema = chartV2SeriesBaseSchema
  .extend({
    seriesType: z.enum(['candlestick', 'bar']),
    data: z.array(chartV2OhlcPointSchema).max(MAX_CHART_POINTS_PER_SERIES),
  })
  .strict();

export const chartBlockPropsSchema = z
  .object({
    series: z
      .array(z.union([chartV2LineSeriesSchema, chartV2OhlcSeriesSchema]))
      .min(1)
      .max(MAX_CHART_SERIES),
    horizon: chartV2HorizonSchema.optional(),
    interval: chartV2IntervalSchema.optional(),
    defaultHorizon: chartV2HorizonSchema.optional(),
    defaultInterval: chartV2IntervalSchema.optional(),
    availableHorizons: z.array(chartV2HorizonSchema).optional(),
    availableIntervals: z.array(chartV2IntervalSchema).optional(),
    disabledHorizons: z.array(chartV2HorizonSchema).optional(),
    enabledIntervals: z.array(chartV2IntervalSchema).optional(),
    theme: chartV2ThemeSchema.optional(),
    locales: chartV2LocalesSchema.optional(),
    colors: z.array(z.string()).optional(),
    showDefaultLegend: z.boolean().optional(),
    showCollapse: z.boolean().optional(),
    showMaximize: z.boolean().optional(),
  })
  .strict();

export const markdownBlockPropsSchema = z
  .object({
    message: z.string().min(1).max(MAX_MARKDOWN_LENGTH),
    isDark: z.boolean().optional(),
  })
  .strict();

const isoDateStringSchema = z
  .string()
  .refine(
    value =>
      /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.+-Z]+)?$/.test(value) &&
      !Number.isNaN(Date.parse(value)),
    { message: 'Expected an ISO date string' }
  );

export const timeSeriesInfoBlockPropsSchema = z
  .object({
    title: z.string().min(1),
    fromLabel: z.string().min(1),
    toLabel: z.string().min(1),
    from: isoDateStringSchema,
    to: isoDateStringSchema,
  })
  .strict();

const priceChangeSchema = z
  .object({
    status: z.enum(['default', 'positive', 'negative']),
    change: z.string(),
    proportionChange: z.string(),
  })
  .strict();

const outsideHoursSchema = z
  .object({
    label: z.string().min(1),
    tag: z.enum(['pre', 'after']),
  })
  .strict();

export const priceDisplayBlockPropsSchema = z
  .object({
    price: z.union([z.string(), z.number()]),
    currency: z.string().min(1),
    priceChange: priceChangeSchema.optional(),
    liveLabel: z.string().optional(),
    isPrimary: z.boolean().optional(),
    outsideHoursText: outsideHoursSchema.optional(),
    showMarketIndicator: z.boolean().optional(),
  })
  .strict();

const allowedImageHosts = new Set(['img.toggle.ai', 'img.864453.co.uk']);

const trustedImageUrlSchema = z
  .string()
  .url()
  .refine(
    value => {
      const url = new URL(value);

      return url.protocol === 'https:' && allowedImageHosts.has(url.hostname);
    },
    {
      message: 'Expected an HTTPS image URL from an approved Toggle image host',
    }
  );

export const assetOverviewHeaderBlockPropsSchema = z
  .object({
    logoUrl: trustedImageUrlSchema.optional(),
    name: z.string().min(1),
    ticker: z.string().min(1),
    price: priceDisplayBlockPropsSchema.optional(),
    isCondensed: z.boolean().optional(),
    showShadow: z.boolean().optional(),
  })
  .strict();

export const calendarBlockLocalesSchema = z
  .object({
    calendar: z.string().min(1).optional(),
    emptyIllustrationAlt: z.string().min(1).optional(),
    emptyTitle: z.string().min(1).optional(),
    emptyDescription: z.string().min(1).optional(),
    seeYesterday: z.string().min(1).optional(),
    viewMoreEvents: z.string().min(1).optional(),
    today: z.string().min(1).optional(),
    noEvents: z.string().min(1).optional(),
    earnings: z.string().min(1).optional(),
    economic: z.string().min(1).optional(),
    company: z.string().min(1).optional(),
    time: z.string().min(1).optional(),
    expected: z.string().min(1).optional(),
    reported: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
  })
  .strict();

export const calendarBlockPropsSchema = z
  .object({
    locales: calendarBlockLocalesSchema.optional(),
    languageSetting: z.string().min(1).optional(),
    economicsEvents: z
      .array(economicEventOutputSchema.strict())
      .max(MAX_CALENDAR_EVENTS_PER_KIND)
      .optional(),
    earningsEvents: z
      .array(earningsItemSchema.strict())
      .max(MAX_CALENDAR_EVENTS_PER_KIND)
      .optional(),
    companyEvents: z
      .array(keyDevsObjectSchema.strict())
      .max(MAX_CALENDAR_EVENTS_PER_KIND)
      .optional(),
    entities: z
      .array(entitySchema.strict())
      .max(MAX_CALENDAR_ENTITIES)
      .optional(),
    isDarkTheme: z.boolean().optional(),
  })
  .strict();

export const cardBlockPropsSchema = z
  .object({
    size: z.enum(['small', 'medium', 'large']).optional(),
  })
  .strict();

const svgIconNameSchema = z.custom<SvgIconNames>(
  value => typeof value === 'string' && isSvgIconName(value),
  { message: 'Expected a design-system icon name' }
);

export const badgeBlockPropsSchema = z
  .object({
    label: z.string().min(1),
    variant: z.enum(['textual', 'numerical']).optional(),
    size: z.enum(['xsmall', 'small', 'medium']).optional(),
    color: z
      .enum(['neutral', 'primary', 'error', 'success', 'warning'])
      .optional(),
    badgeStyle: z.enum(['solid', 'outline']).optional(),
    leftIcon: svgIconNameSchema.optional(),
    rightIcon: svgIconNameSchema.optional(),
  })
  .strict();

export const tableBlockColumnSchema = z
  .object({
    key: z.string().min(1),
    label: z.string().min(1),
    alignment: z.enum(['left', 'right', 'center']).optional(),
  })
  .strict();

export const tableBlockPropsSchema = z
  .object({
    columns: z.array(tableBlockColumnSchema).min(1).max(MAX_TABLE_COLUMNS),
    rows: z
      .array(z.record(z.string(), z.union([z.string(), z.number()])))
      .max(MAX_TABLE_ROWS),
    withBodyRowHover: z.boolean().optional(),
    isHeaderSticky: z.boolean().optional(),
  })
  .strict();

const resizablePanelBlockSchema = z.lazy(() =>
  z
    .object({
      defaultSize: z.string().optional(),
      minSize: z.string().optional(),
      maxSize: z.string().optional(),
      collapsible: z.boolean().optional(),
      collapsedSize: z.string().optional(),
      content: z.array(generativeUiBlockSchema).max(MAX_BLOCKS_PER_LEVEL),
    })
    .strict()
);

export const resizableBlockPropsSchema = z
  .object({
    orientation: z.enum(['horizontal', 'vertical']),
    panels: z.array(resizablePanelBlockSchema).min(2).max(4),
    withHandle: z.boolean().optional(),
    disabled: z.boolean().optional(),
    height: z.number().positive().max(800).optional(),
  })
  .strict();

export const stackBlockPropsSchema = z
  .object({
    gap: z.enum(['sm', 'md', 'lg']).optional(),
  })
  .strict();

export const rowBlockPropsSchema = z
  .object({
    gap: z.enum(['sm', 'md', 'lg']).optional(),
    align: z.enum(['start', 'center', 'stretch']).optional(),
  })
  .strict();

export type ChartBlockProps = z.infer<typeof chartBlockPropsSchema>;
export type MarkdownBlockProps = z.infer<typeof markdownBlockPropsSchema>;
export type TimeSeriesInfoBlockProps = z.infer<
  typeof timeSeriesInfoBlockPropsSchema
>;
export type PriceDisplayBlockProps = z.infer<
  typeof priceDisplayBlockPropsSchema
>;
export type AssetOverviewHeaderBlockProps = z.infer<
  typeof assetOverviewHeaderBlockPropsSchema
>;
export type CalendarBlockLocales = z.infer<typeof calendarBlockLocalesSchema>;
export type CalendarBlockProps = z.infer<typeof calendarBlockPropsSchema>;
export type CardBlockProps = z.infer<typeof cardBlockPropsSchema>;
export type BadgeBlockProps = z.infer<typeof badgeBlockPropsSchema>;
export type TableBlockColumn = z.infer<typeof tableBlockColumnSchema>;
export type TableBlockProps = z.infer<typeof tableBlockPropsSchema>;
export type ResizableBlockProps = z.infer<typeof resizableBlockPropsSchema>;
export type StackBlockProps = z.infer<typeof stackBlockPropsSchema>;
export type RowBlockProps = z.infer<typeof rowBlockPropsSchema>;

export interface AccordionBlockItem {
  title: string;
  content?: GenerativeUiBlock[];
  expandedByDefault?: boolean;
}

export interface AccordionBlockProps {
  items: AccordionBlockItem[];
  collapseAccordionsOnClick?: boolean;
  withBackground?: boolean;
  variant?: 'primary' | 'secondary';
  disabledCollapse?: boolean;
}

export type GenerativeUiBlock =
  | { component: 'Markdown'; props: MarkdownBlockProps }
  | { component: 'Chart'; props: ChartBlockProps }
  | { component: 'TimeSeriesInfo'; props: TimeSeriesInfoBlockProps }
  | { component: 'AssetOverviewHeader'; props: AssetOverviewHeaderBlockProps }
  | { component: 'PriceDisplay'; props: PriceDisplayBlockProps }
  | { component: 'Calendar'; props?: CalendarBlockProps }
  | {
      component: 'Card';
      props?: CardBlockProps;
      children?: GenerativeUiBlock[];
    }
  | { component: 'Badge'; props: BadgeBlockProps }
  | { component: 'Accordion'; props: AccordionBlockProps }
  | { component: 'Table'; props: TableBlockProps }
  | { component: 'Resizable'; props: ResizableBlockProps }
  | {
      component: 'Stack';
      props?: StackBlockProps;
      children?: GenerativeUiBlock[];
    }
  | { component: 'Row'; props?: RowBlockProps; children?: GenerativeUiBlock[] }
  | { component: 'Separator' };

const accordionBlockItemSchema: z.ZodType<AccordionBlockItem> = z.lazy(() =>
  z
    .object({
      title: z.string().min(1),
      content: z
        .array(generativeUiBlockSchema)
        .max(MAX_BLOCKS_PER_LEVEL)
        .optional(),
      expandedByDefault: z.boolean().optional(),
    })
    .strict()
);

export const accordionBlockPropsSchema: z.ZodType<AccordionBlockProps> = z
  .object({
    items: z.array(accordionBlockItemSchema).min(1).max(MAX_BLOCKS_PER_LEVEL),
    collapseAccordionsOnClick: z.boolean().optional(),
    withBackground: z.boolean().optional(),
    variant: z.enum(['primary', 'secondary']).optional(),
    disabledCollapse: z.boolean().optional(),
  })
  .strict();

export interface GenerativeUiBlockPropsMap {
  Markdown: MarkdownBlockProps;
  Chart: ChartBlockProps;
  TimeSeriesInfo: TimeSeriesInfoBlockProps;
  AssetOverviewHeader: AssetOverviewHeaderBlockProps;
  PriceDisplay: PriceDisplayBlockProps;
  Calendar: CalendarBlockProps;
  Card: CardBlockProps & { children?: ReactNode };
  Badge: BadgeBlockProps;
  Accordion: AccordionBlockProps;
  Table: TableBlockProps;
  Resizable: ResizableBlockProps;
  Stack: StackBlockProps & { children?: ReactNode };
  Row: RowBlockProps & { children?: ReactNode };
  Separator: Record<string, never>;
}

export interface GenerativeUiResponse {
  blocks: GenerativeUiBlock[];
}

export const generativeUiBlockSchema: z.ZodType<GenerativeUiBlock> = z.lazy(
  () =>
    z.discriminatedUnion('component', [
      z
        .object({
          component: z.literal('Markdown'),
          props: markdownBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Chart'),
          props: chartBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('TimeSeriesInfo'),
          props: timeSeriesInfoBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('AssetOverviewHeader'),
          props: assetOverviewHeaderBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('PriceDisplay'),
          props: priceDisplayBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Calendar'),
          props: calendarBlockPropsSchema.optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Card'),
          props: cardBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Badge'),
          props: badgeBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Accordion'),
          props: accordionBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Table'),
          props: tableBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Resizable'),
          props: resizableBlockPropsSchema,
        })
        .strict(),
      z
        .object({
          component: z.literal('Stack'),
          props: stackBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Row'),
          props: rowBlockPropsSchema.optional(),
          children: z
            .array(generativeUiBlockSchema)
            .max(MAX_BLOCKS_PER_LEVEL)
            .optional(),
        })
        .strict(),
      z
        .object({
          component: z.literal('Separator'),
        })
        .strict(),
    ])
);

export const generativeUiResponseSchema: z.ZodType<GenerativeUiResponse> =
  z.object({
    blocks: z.array(generativeUiBlockSchema).max(MAX_BLOCKS_PER_LEVEL),
  });
